import {
  V2ListTeamsForAccountApiArg,
  useV2ListTeamsForAccountQuery,
} from "@generated";
import type { BaseRelationship } from "@baseTypes";
import type { Questionnaire, Service, Team } from "../types";

export interface HookArgs {
  page?: V2ListTeamsForAccountApiArg["page"];
  filter?: V2ListTeamsForAccountApiArg["filter"];
}

const useListTeamsWithUsers = ({ page, filter }: HookArgs) => {
  const { data, error, isLoading, refetch } = useV2ListTeamsForAccountQuery({
    include: ["users"],
    page,
    filter,
  });

  let teams: Team[] = [];
  let customError: string | null = null;
  let pageMeta = {
    recordCount: 0,
    pageCount: 0,
  };

  if (data?.data) {
    try {
      teams = data.data.map((team) => {
        if (!team.id) {
          throw new Error("Missing team.id");
        }

        const users = team.relationships.users?.data as BaseRelationship[];
        if (users) {
          for (const user of users) {
            if (!user.id) {
              throw new Error("Missing user.id");
            }
          }
        }

        return {
          id: team.id,
          name: team.attributes.name,
          createdAt: new Date(team.attributes["created-at"]),
          services: (team.attributes.services as Service[]) || [],
          questionnaires:
            (team.attributes.questionnaires as Questionnaire[]) || [],
          users:
            users?.map((user) => ({
              id: user.id,
              name:
                data.included?.find((u) => u.id === user.id)?.attributes
                ?.name ?? "",
            })) ?? [],
        };
      });

      if (!data.meta) {
        throw new Error("Missing data.meta");
      }

      if (!data.meta["record-count"]) {
        throw new Error('Missing data.meta["record-count"]');
      }

      if (!data.meta["page-count"]) {
        throw new Error('Missing data.meta["page-count"]');
      }
      pageMeta = {
        recordCount: data.meta["record-count"],
        pageCount: data.meta["page-count"],
      };
    } catch (err) {
      if (err instanceof Error) {
        customError = err.message;
      } else {
        customError = "An unknown error occurred";
      }
    }
  }

  return { teams, error: error || customError, isLoading, pageMeta, refetch };
};

export default useListTeamsWithUsers;
